import { memo, useMemo } from 'react';
import { ArrowUpRight, Eye, MapPin, ShieldAlert, Sparkles, TrendingUp } from 'lucide-react';
import { formatLabel, riskScore, severityTone } from './semantics';
import type { CompanyNewsAnalysis, RiskItem } from './types';

interface RisksOpportunitiesProps {
  risks: RiskItem[];
  data: CompanyNewsAnalysis;
}

type ScoredRisk = RiskItem & { score: number };

// ─── Priority calculation (severity × likelihood) ──────────────────────────
//
// The backend used to drive priority off severity alone, which collapsed
// "High severity / Low likelihood" and "High severity / High likelihood" into
// the same bucket. Composite scoring keeps both axes visible in the badge.
function priorityFor(score: number): { label: string; pill: string } {
  if (score >= 9) {
    // High×High or High×Medium — top of the heap.
    return { label: 'Critical', pill: 'bg-rose-100 text-rose-800 border border-rose-200' };
  }
  if (score >= 6) {
    return { label: 'High Priority', pill: 'bg-rose-50 text-rose-700 border border-rose-100' };
  }
  if (score >= 3) {
    return { label: 'Monitor', pill: 'bg-amber-50 text-amber-700 border border-amber-100' };
  }
  return { label: 'Watch', pill: 'bg-neutral-50 text-neutral-600 border border-neutral-200' };
}

// ─── Severity / likelihood dot meter ──────────────────────────────────────
//
// 3-dot indicator that fills in proportionally to the rank. Visual scan beats
// reading "High / Medium / Low" when the eye is comparing rows.
function DotMeter({
  label,
  value,
  tone,
}: {
  label: string;
  value?: string | null;
  tone: 'sev' | 'lik';
}) {
  const norm = (value ?? '').trim().toLowerCase();
  const filled = norm === 'high' ? 3 : norm === 'medium' || norm === 'med' ? 2 : norm === 'low' ? 1 : 0;
  const display = value ? formatLabel(value) : '—';
  const filledClass =
    tone === 'sev'
      ? filled === 3
        ? 'bg-rose-500'
        : filled === 2
        ? 'bg-amber-500'
        : 'bg-emerald-500'
      : 'bg-neutral-500';
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-neutral-500">
      <span className="font-medium uppercase tracking-wide text-[10px] text-neutral-400">
        {label}
      </span>
      <span className="inline-flex items-center gap-0.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={`h-1.5 w-1.5 rounded-full ${
              i < filled ? filledClass : 'bg-neutral-200'
            }`}
          />
        ))}
      </span>
      <span className="font-semibold text-neutral-700">{display}</span>
    </span>
  );
}

// ─── Risk card ────────────────────────────────────────────────────────────

function RiskCard({ risk }: { risk: ScoredRisk }) {
  const tone = severityTone(risk.severity);
  const priority = priorityFor(risk.score);
  const isInferred = (risk.origin ?? risk.type ?? '').toLowerCase() === 'inferred';

  return (
    <div className="relative overflow-hidden rounded-lg border border-neutral-200 bg-white">
      {/* Left accent stripe — colour matches severity so a glance down the
          column visually communicates the risk distribution. */}
      <div className={`absolute inset-y-0 left-0 w-1 ${tone.dot}`} aria-hidden />

      <div className="p-4 pl-5">
        {/* Title + composite priority badge */}
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-semibold leading-snug text-neutral-900">
            {risk.risk_name}
          </p>
          <span
            className={`shrink-0 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${priority.pill}`}
          >
            {priority.label}
          </span>
        </div>

        {/* Severity + Likelihood dot meters — single row for visual rhythm. */}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
          <DotMeter label="Severity" value={risk.severity} tone="sev" />
          <DotMeter label="Likelihood" value={risk.likelihood} tone="lik" />
        </div>

        {/* Metadata chips — impact area, horizon, geography, inferred flag.
            Hidden when none are populated. */}
        {(risk.impact_area || risk.time_horizon || risk.geography || isInferred) && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
            {risk.impact_area && (
              <span className="rounded bg-neutral-100 px-1.5 py-0.5 font-medium text-neutral-700">
                {formatLabel(risk.impact_area)}
              </span>
            )}
            {risk.time_horizon && (
              <span className="rounded bg-neutral-100 px-1.5 py-0.5 font-medium text-neutral-700">
                {formatLabel(risk.time_horizon)}
              </span>
            )}
            {risk.geography && (
              <span className="inline-flex items-center gap-0.5 rounded bg-neutral-100 px-1.5 py-0.5 font-medium text-neutral-700">
                <MapPin className="h-2.5 w-2.5 text-neutral-500" />
                {risk.geography}
              </span>
            )}
            {isInferred && (
              <span
                className="inline-flex items-center gap-0.5 rounded border border-dashed border-neutral-300 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-neutral-500"
                title="Risk inferred by the analyst — not stated directly in any single article"
              >
                <Eye className="h-2.5 w-2.5" />
                Inferred
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const MemoRiskCard = memo(RiskCard);

// ─── Opportunity card ─────────────────────────────────────────────────────
//
// `bullish_factors[]` is a flat string array — no metadata to render — so the
// card stays simple. The emerald accent stripe + sparkle icon gives it
// enough visual weight to pair against the heavier risk column.

function OpportunityCard({ text }: { text: string }) {
  return (
    <div className="relative overflow-hidden rounded-lg border border-neutral-200 bg-white">
      <div className="absolute inset-y-0 left-0 w-1 bg-emerald-400" aria-hidden />
      <div className="flex items-start gap-2.5 p-4 pl-5">
        <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
        <p className="text-[13px] leading-relaxed text-neutral-700">{text}</p>
      </div>
    </div>
  );
}

// ─── Section ──────────────────────────────────────────────────────────────

function RisksOpportunitiesBase({ risks, data }: RisksOpportunitiesProps) {
  // Score every risk (severity × likelihood) and sort highest-impact first.
  // Render the full set — the backend prompt now scales risk count to corpus
  // size (4-7 for ≥30 analyzed articles), so an arbitrary slice cap drops
  // material content.
  const scored = useMemo<ScoredRisk[]>(
    () =>
      [...risks]
        .map((r) => ({ ...r, score: riskScore(r.severity, r.likelihood) }))
        .sort((a, b) => b.score - a.score),
    [risks]
  );

  const opportunities = data.investment_interpretation?.bullish_factors ?? [];

  if (scored.length === 0 && opportunities.length === 0) return null;

  const bothCols = scored.length > 0 && opportunities.length > 0;

  // Aggregate counts for the section header — gives users a quick read on
  // whether the risk surface is concentrated in any priority tier.
  const criticalCount = scored.filter((r) => priorityFor(r.score).label === 'Critical').length;
  const highCount = scored.filter((r) => priorityFor(r.score).label === 'High Priority').length;
  const monitorCount = scored.filter((r) => priorityFor(r.score).label === 'Monitor').length;
  const watchCount = scored.filter((r) => priorityFor(r.score).label === 'Watch').length;

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-widest text-neutral-800">
            Strategic Risks &amp; Opportunities
          </h2>
          <p className="mt-1 text-xs text-neutral-500">
            Factors requiring management attention and areas with growth potential
          </p>
        </div>
        {scored.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 text-[11px] font-semibold text-neutral-500">
            <span>{scored.length} {scored.length === 1 ? 'risk' : 'risks'}</span>
            {criticalCount > 0 && (
              <span className="inline-flex items-center gap-1 text-rose-800">
                <span className="h-1.5 w-1.5 rounded-full bg-rose-600" />
                {criticalCount} critical
              </span>
            )}
            {highCount > 0 && (
              <span className="inline-flex items-center gap-1 text-rose-700">
                <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                {highCount} high
              </span>
            )}
            {monitorCount > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-700">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                {monitorCount} monitor
              </span>
            )}
            {watchCount > 0 && (
              <span className="inline-flex items-center gap-1 text-neutral-600">
                <span className="h-1.5 w-1.5 rounded-full bg-neutral-400" />
                {watchCount} watch
              </span>
            )}
            {opportunities.length > 0 && (
              <>
                <span className="text-neutral-300">·</span>
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <Sparkles className="h-3 w-3" />
                  {opportunities.length} {opportunities.length === 1 ? 'opportunity' : 'opportunities'}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {/* 3/2 grid when both columns are populated — risks are richer cards
          and deserve more horizontal real estate than the simple opportunity
          one-liners. Falls back to single column when one side is empty. */}
      <div
        className={`grid gap-5 ${
          bothCols ? 'lg:grid-cols-5' : 'grid-cols-1'
        }`}
      >
        {scored.length > 0 && (
          <div className={bothCols ? 'lg:col-span-3' : ''}>
            <div className="mb-3 flex items-center gap-2">
              <ShieldAlert className="h-3.5 w-3.5 text-rose-500" />
              <p className="text-[10px] font-bold uppercase tracking-widest text-rose-700">
                Risks Requiring Attention
              </p>
            </div>
            <div className="space-y-2.5">
              {scored.map((r) => (
                <MemoRiskCard key={r.risk_id} risk={r} />
              ))}
            </div>
          </div>
        )}

        {opportunities.length > 0 && (
          <div className={bothCols ? 'lg:col-span-2' : ''}>
            <div className="mb-3 flex items-center gap-2">
              <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                Growth Opportunities
              </p>
            </div>
            <div className="space-y-2.5">
              {opportunities.map((opp, i) => (
                <OpportunityCard key={i} text={opp} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export const RisksOpportunities = memo(RisksOpportunitiesBase);
